import { UseFormReturn, FieldValues, Path } from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "@altimateai/lego";
import { OwnershipConditionKeys, OwnershipConditionTypes, OwnershipEntities } from "./types";
import AsyncEntitySelector from "@/components/AsyncEntitySelector";

interface OwnershipConditionValueFieldProps<TFieldValues extends FieldValues> {
  form: UseFormReturn<TFieldValues>;
  index: number;
  userOptions: { label: string; value: string }[];
}

/**
 * Maps entity type and condition key to the API entity type for AsyncEntitySelector.
 * This is needed because condition keys like "name" are generic and need context
 * from the entity type to determine which API endpoint to call.
 */
const getApiEntityType = (
  entityType: OwnershipEntities,
  conditionKey: OwnershipConditionKeys
): string => {
  // For query entity type, the condition key itself contains the info we need
  if (entityType === OwnershipEntities.QUERY) {
    switch (conditionKey) {
      case OwnershipConditionKeys.QUERY_WAREHOUSE_NAME:
        return "warehouse";
      case OwnershipConditionKeys.QUERY_USER:
        return "user";
      case OwnershipConditionKeys.QUERY_ROLE:
        return "role";
      default:
        return conditionKey;
    }
  }

  // For other entity types with "name" condition key, use the entity type directly
  if (conditionKey === OwnershipConditionKeys.WAREHOUSE_NAME) {
    // "name" is used for warehouse, database, and schema - use entity type to distinguish
    switch (entityType) {
      case OwnershipEntities.WAREHOUSE:
        return "warehouse";
      case OwnershipEntities.DATABASE:
        return "database";
      case OwnershipEntities.SCHEMA:
        return "schema";
      case OwnershipEntities.TABLE:
        return "table";
      default:
        return entityType;
    }
  }

  return conditionKey;
};

const OwnershipConditionValueField = <TFieldValues extends FieldValues>({
  form,
  index,
  userOptions,
}: OwnershipConditionValueFieldProps<TFieldValues>) => {
  const entityType = form.watch(
    `ownerships.${index}.entity_type` as Path<TFieldValues>
  ) as OwnershipEntities;
  const conditionKey = form.watch(
    `ownerships.${index}.condition_key` as Path<TFieldValues>
  ) as OwnershipConditionKeys;
  const conditionType = form.watch(
    `ownerships.${index}.condition_type` as Path<TFieldValues>
  ) as OwnershipConditionTypes;

  // Check if this condition key should show dropdown for equals/in condition types
  const dropdownConditionKeys = [
    OwnershipConditionKeys.WAREHOUSE_NAME,
    OwnershipConditionKeys.QUERY_WAREHOUSE_NAME,
    OwnershipConditionKeys.DATABASE_NAME,
    OwnershipConditionKeys.SCHEMA_NAME,
    OwnershipConditionKeys.QUERY_USER,
    OwnershipConditionKeys.QUERY_ROLE,
  ] as OwnershipConditionKeys[];

  const shouldShowDropdown =
    (conditionType === OwnershipConditionTypes.EQUALS ||
      conditionType === OwnershipConditionTypes.IN) &&
    dropdownConditionKeys.includes(conditionKey as OwnershipConditionKeys);

  const isMultiSelect = conditionType === OwnershipConditionTypes.IN;

  // For user condition key, use static options from hook
  const userOptionsForCombobox =
    conditionKey === OwnershipConditionKeys.QUERY_USER ? userOptions : [];

  if (shouldShowDropdown) {
    // For user, use static options; for others, AsyncEntitySelector will handle API calls internally
    const staticOptions =
      conditionKey === OwnershipConditionKeys.QUERY_USER ? userOptionsForCombobox : [];

    // Map the entity type and condition key to the correct API entity type
    const apiEntityType = getApiEntityType(entityType, conditionKey);

    return (
      <AsyncEntitySelector
        entityType={apiEntityType}
        staticOptions={staticOptions}
        isMultiSelect={isMultiSelect}
        form={form}
        fieldName={`ownerships.${index}.condition_value` as Path<TFieldValues>}
        label="Value"
      />
    );
  }

  // Default to text input for other cases
  return (
    <FormField
      control={form.control}
      name={`ownerships.${index}.condition_value` as Path<TFieldValues>}
      render={({ field }) => (
        <FormItem>
          <FormLabel>Value</FormLabel>
          <FormControl>
            <Input placeholder="Enter condition value" {...field} className="!al-m-0" />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export default OwnershipConditionValueField;
